import React, { useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const API_URL = process.env.REACT_APP_API_URL;

const formatPostDate = (value) => {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d)) return value;
  return d.toISOString().split("T")[0]; 
};

const ProjectReport = () => {
  const [projectId, setProjectId] = useState("");
  const [projectInfo, setProjectInfo] = useState(null);
  const [activities, setActivities] = useState([]);
  const [error, setError] = useState("");

  const fetchReport = async () => {
    setError("");
    setProjectInfo(null);
    setActivities([]);

    if (!projectId.trim()) {
      setError("Please enter a valid Project ID");
      return;
    }

    try {
      const res = await fetch(`${API_URL}/project-report/${encodeURIComponent(projectId.trim())}`);
      const data = await res.json();

      if (!res.ok || data.error) {
        setError(data.error || "Error fetching report");
        return;
      }

      setProjectInfo(data.project);

      // Filter out users with both review_wordcount and postedit_wordcount as 0 or missing
      const filtered = (data.activities || []).filter(
        (a) => (a.review_wordcount || 0) > 0 || (a.postedit_wordcount || 0) > 0
      );

      setActivities(filtered);
    } catch (e) {
      setError("Failed to fetch report.");
    }
  };

  // Calculate totals for summary
  const totalReviewWordcount = activities.reduce(
    (acc, act) => acc + (parseInt(act.review_wordcount) || 0),
    0
  );
  const totalPosteditWordcount = activities.reduce(
    (acc, act) => acc + (parseInt(act.postedit_wordcount) || 0),
    0
  );
  const totalWordcount = totalReviewWordcount + totalPosteditWordcount;
  const uniqueUserCount = new Set(activities.map((a) => a.userid)).size;

  const downloadPDF = () => {
    if (!projectInfo || !activities.length) return;

    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text(`Project Report - ${projectId}`, 14, 15);

    const headerY = 25;
    doc.setFontSize(10);
    doc.text(`Project Name: ${projectInfo.projectname || "-"}`, 14, headerY);
    doc.text(`Client ID: ${projectInfo.clientid || "-"}`, 14, headerY + 6);
    doc.text(`Start Date: ${formatPostDate(projectInfo.startdate)}`, 14, headerY + 12);
    doc.text(`Completion Date: ${formatPostDate(projectInfo.completedate)}`, 14, headerY + 18)

    autoTable(doc, {
      startY: headerY + 25,
      head: [["User ID", "User Name", "Activity", "Word Count"]],
      body: activities.flatMap((a) => {
        const rows = [];
        if ((a.review_wordcount || 0) > 0) {
          rows.push([a.userid || "-", a.user_name || "-", "review", a.review_wordcount || 0]);
        }
        if ((a.postedit_wordcount || 0) > 0) {
          rows.push([a.userid || "-", a.user_name || "-", "postedit", a.postedit_wordcount || 0]);
        }
        return rows;
      }),
      styles: { fontSize: 9 },
      headStyles: { fillColor: [41, 128, 185], textColor: 255 },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "left" },
        2: { halign: "center" },
        3: { halign: "center" },
      },
    });

    const summaryY = (doc.lastAutoTable?.finalY || headerY + 50) + 10;
    doc.setFontSize(10);
    doc.setFont("helvetica", "bold");
    doc.text(`Total Users Worked: ${uniqueUserCount}`, 14, summaryY);
    doc.text(`Total Word Count (Postedit): ${totalPosteditWordcount}`, 14, summaryY + 6);
    doc.text(`Total Word Count (Review): ${totalReviewWordcount}`, 14, summaryY + 12);
    doc.text(`Total Word Count: ${totalWordcount}`, 14, summaryY + 18);

    doc.save(`Project_Report_${projectId}.pdf`);
  };

  return (
    <div className="container mt-4">
      <h3>Project-wise Report Generator</h3>

      <div className="row mb-3">
        <div className="col-md-6">
          <input
            type="text"
            className="form-control"
            placeholder="Enter Project ID"
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
          />
        </div>
        <div className="col-md-3">
          <button className="btn btn-primary" onClick={fetchReport}>
            Fetch Report
          </button>
        </div>
        {activities.length > 0 && (
          <div className="col-md-3 text-end">
            <button className="btn btn-success" onClick={downloadPDF}>
              Download PDF
            </button>
          </div>
        )}
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {projectInfo && (
        <div className="mb-4 p-3 bg-light border rounded">
          <h5>Project Info</h5>
          <p>
            <strong>Project ID:</strong> {projectInfo.projectid}
          </p>
          <p>
            <strong>Project Name:</strong> {projectInfo.projectname}
          </p>
          <p>
            <strong>Client ID:</strong> {projectInfo.clientid}
          </p>
          <p>
            <strong>Start Date:</strong> {formatPostDate(projectInfo.startdate)}
          </p>
          <p>
            <strong>Complete Date:</strong> {formatPostDate(projectInfo.completedate)}
          </p>
        </div>
      )}

      {activities.length > 0 && (
        <>
          <h5>Activities</h5>
          <div className="table-responsive mb-4">
            <table className="table table-bordered table-striped">
              <thead className="table-dark">
                <tr>
                  <th>User ID</th>
                  <th>User Name</th>
                  <th>Activity</th>
                  <th>Word Count</th>
                </tr>
              </thead>
              <tbody>
                {activities.flatMap((a, idx) => {
                  const rows = [];
                  if ((a.review_wordcount || 0) > 0) {
                    rows.push(
                      <tr key={`${idx}-review`}>
                        <td>{a.userid}</td>
                        <td>{a.user_name}</td>
                        <td>review</td>
                        <td>{a.review_wordcount}</td>
                      </tr>
                    );
                  }
                  if ((a.postedit_wordcount || 0) > 0) {
                    rows.push(
                      <tr key={`${idx}-postedit`}>
                        <td>{a.userid}</td>
                        <td>{a.user_name}</td>
                        <td>postedit</td>
                        <td>{a.postedit_wordcount}</td>
                      </tr>
                    );
                  }
                  return rows;
                })}
              </tbody>
            </table>
          </div>

          <div className="p-3 border bg-light">
            <h5>Summary</h5>
            <p>
              <strong>Total Users Worked:</strong> {uniqueUserCount}
            </p>
            <p>
              <strong>Total Word Count (Postedit):</strong> {totalPosteditWordcount}
            </p>
            <p>
              <strong>Total Word Count (Review):</strong> {totalReviewWordcount}
            </p>
            <p>
              <strong>Total Word Count:</strong> {totalWordcount}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectReport;
